import React from 'react';
import { ArrowRight, Target, Heart, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <div className="bg-eclipse-bg">
      {/* Hero Section */}
      <section
        className="relative py-24 md:py-32 overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at top right, rgba(0, 217, 255, 0.15), transparent 40%), radial-gradient(circle at bottom left, rgba(255, 140, 66, 0.1), transparent 40%)',
        }}
        data-testid="about-hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-cyan mb-4">
              About Us
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none mb-6">
              Excellence Through{' '}
              <span className="bg-gradient-to-r from-brand-cyan to-brand-orange bg-clip-text text-transparent">
                Perfect Alignment
              </span>
            </h1>
            <p className="text-lg lg:text-xl text-eclipse-secondary leading-relaxed">
              At EclipseProfessionals, we believe that excellence happens when strategy, creativity,
              and execution align perfectly — just like an eclipse.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 md:py-32" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1701486485362-2590bbfc7e00"
                alt="Our Mission"
                className="rounded-2xl shadow-2xl shadow-brand-cyan/10"
                data-testid="mission-image"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="inline-flex p-3 bg-brand-cyan/10 rounded-full mb-6">
                <Target className="w-8 h-8 text-brand-cyan" />
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                Our Mission
              </h2>
              <p className="text-eclipse-secondary leading-relaxed mb-6">
                Our mission is to help businesses rise above the ordinary through dependable
                expertise, thoughtful design, and results-driven solutions.
              </p>
              <p className="text-eclipse-secondary leading-relaxed mb-6">
                We are a multidisciplinary team specializing in graphic design, consultancy, digital
                support, and business optimization. Whether you're building a brand, improving your
                operations, or elevating your online presence, we deliver work that is precise,
                professional, and impactful.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-eclipse-surface" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              What We Stand For
            </h2>
            <p className="text-eclipse-secondary max-w-2xl mx-auto leading-relaxed">
              Our values guide every decision we make and every project we undertake.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div
              className="bg-eclipse-bg border border-white/5 rounded-2xl p-8 hover:-translate-y-1 hover:border-brand-cyan/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.1)] transition-all duration-300"
              data-testid="value-card-1"
            >
              <div className="inline-flex p-3 bg-brand-cyan/10 rounded-full mb-6">
                <Award className="w-8 h-8 text-brand-cyan" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Excellence</h3>
              <p className="text-eclipse-secondary leading-relaxed">
                We don't settle for good enough. Every project is executed with precision and
                attention to detail, ensuring professional, polished results.
              </p>
            </div>

            <div
              className="bg-eclipse-bg border border-white/5 rounded-2xl p-8 hover:-translate-y-1 hover:border-brand-orange/50 hover:shadow-[0_0_30px_rgba(255,140,66,0.1)] transition-all duration-300"
              data-testid="value-card-2"
            >
              <div className="inline-flex p-3 bg-brand-orange/10 rounded-full mb-6">
                <Heart className="w-8 h-8 text-brand-orange" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Dedication</h3>
              <p className="text-eclipse-secondary leading-relaxed">
                Your success is our success. We're committed to understanding your goals and
                delivering solutions that drive measurable results.
              </p>
            </div>

            <div
              className="bg-eclipse-bg border border-white/5 rounded-2xl p-8 hover:-translate-y-1 hover:border-brand-cyan/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.1)] transition-all duration-300"
              data-testid="value-card-3"
            >
              <div className="inline-flex p-3 bg-brand-cyan/10 rounded-full mb-6">
                <Target className="w-8 h-8 text-brand-cyan" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Impact</h3>
              <p className="text-eclipse-secondary leading-relaxed">
                We create systems that work. Every solution is designed to solve real problems and
                create lasting, positive change for your business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 md:py-32" data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                We Don't Just Complete Tasks
              </h2>
              <p className="text-eclipse-secondary leading-relaxed mb-6">
                Clients trust us because we don't just complete tasks — we solve problems, elevate
                brands, and create systems that work.
              </p>
              <p className="text-eclipse-secondary leading-relaxed mb-6">
                Every project is handled with dedication, clarity, and a commitment to measurable
                results.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-brand-cyan rounded-full mt-2"></div>
                  <p className="text-eclipse-secondary">
                    <strong className="text-eclipse-text">Solve Problems:</strong> We dig deep to
                    understand root causes and deliver effective solutions.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-brand-orange rounded-full mt-2"></div>
                  <p className="text-eclipse-secondary">
                    <strong className="text-eclipse-text">Elevate Brands:</strong> We help businesses
                    stand out with compelling visual identity and messaging.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-brand-cyan rounded-full mt-2"></div>
                  <p className="text-eclipse-secondary">
                    <strong className="text-eclipse-text">Create Systems:</strong> We build
                    sustainable processes that drive long-term success.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-eclipse-surface border border-white/5 rounded-2xl p-8">
                <blockquote className="text-xl italic text-eclipse-text mb-6">
                  "At EclipseProfessionals, we don't aim to be seen. We aim to make you stand out."
                </blockquote>
                <p className="text-eclipse-secondary">
                  This philosophy drives everything we do. Your success is our measure of excellence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          background:
            'radial-gradient(circle at center, rgba(0, 217, 255, 0.1), transparent 50%)',
        }}
        data-testid="about-cta-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-eclipse-secondary text-lg leading-relaxed mb-8">
            Let's create something exceptional together. Reach out today to start your project.
          </p>
          <Link
            to="/contact"
            data-testid="about-contact-cta"
            className="inline-flex items-center gap-2 bg-brand-cyan text-eclipse-bg font-bold px-10 py-5 rounded-full hover:bg-white hover:scale-105 transition-all duration-300 text-lg"
          >
            Get in Touch
            <ArrowRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;